import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { AsyncJobDetailOutDTO, JobStatusEnum, JobTypeEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { useJobEvents, useJobProgress } from '@hooks/useJobs/useJobs.ts';
import { PushContext } from '@hooks/usePush/PushContext.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import JobDetailHeader, { JobExpiryNote } from '@components/ringil3/Features/Job/JobDetailHeader.tsx';
import SvgOK from '@icons/check-circle.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import { triggerHiddenIframeDownload } from '@utils/downloadViaHiddenIframe';

interface ComponentProps {
  jobId: string;
  /** Detail the page already fetched to resolve the job type; seeds state so the mount does not refetch. */
  initialDetail?: AsyncJobDetailOutDTO;
}

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];

/**
 * Detail of an EXPORT async job: a progress bar while running and a download button when done.
 * Live source = jobs push hints (company-jobs/personal channels) via useJobProgress; one
 * reconciliation GET on finish.
 */
const ExportJobDetail: React.FC<ComponentProps> = ({ jobId, initialDetail }) => {
  const { getAsyncJobDetail, cancelAsyncJob, downloadExportAsyncJob, retryJob, archiveJob } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const nav = useNavigation();
  const [detail, setDetail] = useState<AsyncJobDetailOutDTO>(initialDetail);
  // Key of the job action in flight (RD-2954). The clicked button spins (RingilButton swallows clicks
  // while `loading`) and the other action buttons grey out, so neither a double click nor a parallel
  // action sends a second request — that used to come back from the BE as a 409.
  // The key is cleared only once the detail GET that follows the mutation has landed: on a fast
  // (local) backend the POST can return before the second click of a double click, and until the
  // refetched status re-renders the page the old button is still there and would fire again.
  const [action, setAction] = useState<string | null>(null);
  const busy = (key: string) => ({ loading: action === key, disabled: action != null && action !== key });
  const abortRef = useRef<AbortController | null>(null);

  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Download / cancel jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const load = (onLoaded?: () => void) => {
    // Capture the controller of THIS jobId's effect: a jobId switch must not let the previous
    // job's in-flight response through (abortRef.current would already point at the new one).
    const ac = abortRef.current;
    getAsyncJobDetail(jobId, d => {
      if (ac?.signal.aborted) return;
      setDetail(d);
      onLoaded?.();
    });
  };

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    // The page fetched this job to decide which detail to render, so on open we already have it;
    // refreshes (push hints, poll, finish) still go through load().
    if (initialDetail == null) load();
    return () => ac.abort();
  }, [jobId]);

  const live = useJobProgress(jobId);
  useJobEvents(jobId, {
    onFinished: () => {
      // onFinished also fires for a job that was already terminal when the page opened. Only
      // reconcile when we know the detail is stale; while it is still null the initial load is
      // in flight and would simply be duplicated.
      if (detail != null && !TERMINAL.includes(detail.job.jobStatus)) load();
    },
    onReconnect: () => load(),
  });

  // REST is the source of truth once terminal: a stale push snapshot (e.g. colleague's job with
  // push down) must not keep the bar "running" after the detail says DONE.
  const detailTerminal = detail != null && TERMINAL.includes(detail.job.jobStatus);
  const status = detailTerminal ? detail.job.jobStatus : ((live?.status as JobStatusEnum | undefined) ?? detail?.job?.jobStatus);
  const isRunning = status === JobStatusEnum.PENDING || status === JobStatusEnum.RUNNING;
  const isTerminal = status != null && TERMINAL.includes(status);

  // Poll fallback while running: push hints are primary; 15s when the push connection is down
  // (also covers colleagues' jobs, which the central my-jobs reseed doesn't), 60s insurance when up.
  const { connectionState } = useContext(PushContext);
  const pushLive = connectionState === 'connected';
  useEffect(() => {
    if (detail == null || !isRunning) return;
    const id = window.setInterval(load, pushLive ? 60000 : 15000);
    return () => window.clearInterval(id);
  }, [jobId, isRunning, pushLive, detail == null]);

  if (detail == null) {
    return <SvgLoader className={'animate-spin'} />;
  }

  const job = detail.job;
  const processed = detailTerminal ? job.processedCount : live?.processed ?? job.processedCount;
  const total = detailTerminal ? job.totalCount : live?.total ?? job.totalCount;
  const pct = total != null && total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;

  const descriptor = safeParse(job.resultJson);
  const downloadReady = typeof descriptor?.artifactUrl === 'string' && descriptor.artifactUrl.length > 0;
  const title = `${tr('JobDetail.task', 'Úkol')} ${job.applicationId ?? ''}`;

  const retry = () => {
    setAction('retry');
    retryJob(
      jobId,
      () => load(() => setAction(null)),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const download = () => {
    setAction('download');
    downloadExportAsyncJob(
      jobId,
      d => {
        setAction(null);
        if (d?.url) {
          triggerHiddenIframeDownload(d.url, () => showToastMessage(tr('JobList.downloadFailed', 'Stažení se nepodařilo'), '', 'error'));
        } else {
          showToastMessage(tr('JobList.downloadNotReady', 'Soubor ještě není připraven.'), '', 'warn');
        }
      },
      null,
      {
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const cancel = () => {
    setAction('cancel');
    cancelAsyncJob(
      jobId,
      () => load(() => setAction(null)),
      null,
      {
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const archive = () => {
    setAction('archive');
    archiveJob(jobId, () => nav.navigate(nav.urlFunctions.createJobList()), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          { label: tr('JobList.title', 'Rozpracované úkoly'), url: nav.createNavigationLink(nav.urlFunctions.createJobList()) },
          { label: title, disabled: true },
        ]}
      />

      <JobDetailHeader
        title={title}
        jobTypeLabel={tr(`JobList.typeBadge.${JobTypeEnum.EXPORT}`, 'Export')}
        jobCategory={detail.jobCategory}
        createdByName={detail.createdByName}
        createdAt={job.createdAt}
        onCancel={isRunning && jobsRwFlag.granted ? cancel : undefined}
        onArchive={isTerminal && jobsRwFlag.granted ? archive : undefined}
        busyAction={action}
      />

      {isRunning && (
        <div className={'flex flex-col gap-4'}>
          <COInlineMessage variant={'warning'} contentClassName={'flex-1 text-center'}>
            {tr('JobDetail.export.inProgress', 'Export se zpracovává')}
          </COInlineMessage>
          <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
            <SvgLoader className={'animate-spin'} />
            <span className={'text-content-secondary'}>{tr('JobList.processedPct', '{{pct}} % zpracováno', { pct })}</span>
            <div className={'w-60 bg-base-quaternary rounded h-2'}>
              <div className={'bg-content-brand h-2 rounded'} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      )}

      {status === JobStatusEnum.DONE && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
          <div className={'flex flex-row items-center gap-2'}>
            <SvgRobot width={32} height={32} />
            <SvgOK className={'text-complementary-success'} />
            <span>{tr('JobDetail.export.done', 'Export je úspěšně dokončen.')}</span>
          </div>
          {downloadReady && jobsRwFlag.granted && (
            <RingilButton
              mode={'filled-rectangle'}
              label={tr('JobList.download', 'Stáhnout')}
              dataTestId={'export-job-download'}
              onClick={download}
              {...busy('download')}
            />
          )}
        </div>
      )}

      {status === JobStatusEnum.ERROR && (
        <div className={'flex flex-col gap-4'}>
          <COInlineMessage variant={'error'} contentClassName={'flex-1 text-center'}>
            <div>{tr('JobDetail.export.failed', 'Export se nepodařilo vytvořit.')}</div>
            {job.errorMessage != null && <div>{job.errorMessage}</div>}
          </COInlineMessage>
          {jobsRwFlag.granted && (
            <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
              <RingilButton
                mode={'filled-rectangle'}
                label={tr('JobList.retry', 'Zkusit znovu')}
                dataTestId={'export-job-retry'}
                onClick={retry}
                {...busy('retry')}
              />
            </div>
          )}
        </div>
      )}

      {status === JobStatusEnum.CANCELLED && (
        <div className={'flex flex-col items-start'}>
          <COBadge variant={'filled'} type={'draft'}>{tr('ExportJobDetail.cancelled', 'Zrušeno')}</COBadge>
        </div>
      )}

      {isTerminal && <JobExpiryNote expiresAt={detail.expiresAt} />}
    </div>
  );
};

const safeParse = (json?: string): any | null => {
  if (json == null) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export default ExportJobDetail;
